import React, { useState } from "react";
import Modal from "Components/Common/GenericModal";
import { Form, FormGroup, Label, Input, Row, Col } from "reactstrap";
import DateTimePicker from "Components/Common/DateTimePicker";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./AddGuestReqModal.scss";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AddGuestRequest,
  GetAllCondos,
  GetAllResidents,
} from "endpoints/guestAccess/index.action";
import Spinner from "Components/Common/Spinner";
import { GuestRequestFormValues } from "types/guestModule";
import formatTo12h from "utils/formatTimeto12h";
import Button from "Components/Common/Button";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  refetchRequests: () => void;
}

const AddGuestReqModal: React.FC<Props> = ({
  isOpen,
  onClose,
  refetchRequests,
}) => {
  const { t } = useTranslation();
  const [residents, setResidents] = useState<
    { id: string; firstName: string; lastName: string }[]
  >([]);
  const [condos, setCondos] = useState<{ id: string; name: string }[]>([]);
  const { isPending } = useQuery({
    queryKey: ["residentsList"],
    queryFn: async () => {
      const res = await GetAllResidents();
      if (res && res.data && Array.isArray(res.data)) {
        setResidents(res.data);
      }
      return [];
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  const { isPending: isCondosFetching } = useQuery({
    queryKey: ["condosList"],
    queryFn: async () => {
      const res = await GetAllCondos();
      if (res && res.data && Array.isArray(res.data)) {
        setCondos(res.data);
      }
      return [];
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  const addGuestMutation = useMutation({
    mutationKey: ["addGuestRequest"],
    mutationFn: async (values: GuestRequestFormValues) => {
      const res = await AddGuestRequest(values);
      return res;
    },
  });

  const formik = useFormik({
    initialValues: {
      condoId: "",
      residentId: "",
      startDate: "",
      guestName: "",
      endDate: "",
      startTime: "",
      endTime: "",
      notifyMe: false,
      purposeOfVisit: "",
    },
    validationSchema: Yup.object({
      condoId: Yup.string().required(t("accessRequests.pleaseChooseCondominium")),
      residentId: Yup.string().required(t("accessRequests.pleaseChooseResident")),
      startDate: Yup.string().required(t("accessRequests.startDateRequired")),
      guestName: Yup.string().required(t("accessRequests.guestNameRequired")),
      endDate: Yup.string().required(t("accessRequests.endDateRequired")),
      startTime: Yup.string().required(t("accessRequests.startTimeRequired")),
      endTime: Yup.string().required(t("accessRequests.endTimeRequired")),
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        ...values,
        startTime: formatTo12h(values.startTime),
        endTime: formatTo12h(values.endTime),
      } as GuestRequestFormValues;
      const response = await addGuestMutation.mutateAsync(payload);
      if (response && response?.statusCode === 201) {
        toast.success(t("accessRequests.guestRequestAdded"));
        resetForm();
        onClose();
        refetchRequests();
      } else {
        toast.error(
          response?.message ||
            response?.response ||
            t("accessRequests.failedToAddGuestRequest")
        );
      }
    },
  });

  if (isPending || isCondosFetching) {
    return <Spinner />;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closable={false}
      title={t("accessRequests.addNewGuestRequest")}
      description={t("accessRequests.grantAccessDescription")}
      size="md"
    >
      <Form onSubmit={formik.handleSubmit} className="add-locker-form">
        <FormGroup>
          <Label for="condoId">{t("accessRequests.condo")}</Label>
          <Input
            type="select"
            id="condoId"
            name="condoId"
            value={formik.values.condoId}
            onChange={formik.handleChange}
            invalid={!!(formik.touched.condoId && formik.errors.condoId)}
          >
            <option value="">{t("accessRequests.chooseCondominium")}</option>
            {condos.map((r) => (
              <option key={r.id} value={r.id}>
                {r?.name}
              </option>
            ))}
          </Input>
          {formik.touched.condoId && formik.errors.condoId && (
            <div className="text-danger small mt-1">
              {formik.errors.condoId}
            </div>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="residentId">{t("accessRequests.resident")}</Label>
          <Input
            type="select"
            id="residentId"
            name="residentId"
            value={formik.values.residentId}
            onChange={formik.handleChange}
            invalid={!!(formik.touched.residentId && formik.errors.residentId)}
          >
            <option value="">{t("accessRequests.chooseResident")}</option>
            {residents.map((r) => (
              <option key={r.id} value={r.id}>
                {r?.firstName} {r?.lastName}
              </option>
            ))}
          </Input>
          {formik.touched.residentId && formik.errors.residentId && (
            <div className="text-danger small mt-1">
              {formik.errors.residentId}
            </div>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="resident">{t("accessRequests.guestName")}</Label>
          <Input
            type="text"
            id="guestName"
            name="guestName"
            value={formik.values.guestName}
            onChange={formik.handleChange}
            invalid={!!(formik.touched.guestName && formik.errors.guestName)}
          ></Input>
          {formik.touched.guestName && formik.errors.guestName && (
            <div className="text-danger small mt-1">
              {formik.errors.guestName}
            </div>
          )}
        </FormGroup>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>{t("accessRequests.startingDate")}</Label>
              <div className="flat-wrapper">
                <DateTimePicker
                  className="form-control"
                  placeholder={t("accessRequests.selectDate")}
                  mode="date"
                  value={formik.values.startDate}
                  onChange={(v) => formik.setFieldValue("startDate", v)}
                />
                <i className="ri-calendar-line field-icon"></i>
              </div>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>{t("accessRequests.endingDate")}</Label>
              <div className="flat-wrapper">
                <DateTimePicker
                  className="form-control"
                  placeholder={t("accessRequests.selectDate")}
                  mode="date"
                  value={formik.values.endDate}
                  onChange={(v) => formik.setFieldValue("endDate", v)}
                />
                <i className="ri-calendar-line field-icon"></i>
              </div>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>{t("accessRequests.startingTime")}</Label>
              <div className="flat-wrapper">
                <DateTimePicker
                  className="form-control"
                  placeholder={t("accessRequests.selectTime")}
                  mode="time"
                  value={formik.values.startTime}
                  onChange={(v) => formik.setFieldValue("startTime", v)}
                />
                <i className="ri-time-line field-icon"></i>
              </div>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>{t("accessRequests.endingTime")}</Label>
              <div className="flat-wrapper">
                <DateTimePicker
                  className="form-control"
                  placeholder={t("accessRequests.selectTime")}
                  mode="time"
                  value={formik.values.endTime}
                  onChange={(v) => formik.setFieldValue("endTime", v)}
                />
                <i className="ri-time-line field-icon"></i>
              </div>
            </FormGroup>
          </Col>
          <p className="text-muted fs-10">
            {t("accessRequests.otpValidDuringTime")}
          </p>
        </Row>

        <FormGroup check className="mb-3">
          <Input
            type="checkbox"
            id="notifyMe"
            name="notifyMe"
            checked={formik.values.notifyMe}
            onChange={(e) =>
              formik.setFieldValue(
                "notifyMe",
                (e.target as HTMLInputElement).checked
              )
            }
          />
          <Label for="notifyMe" className="ms-2 text">
            {t("accessRequests.notifyMeOnEntry")}
          </Label>
        </FormGroup>

        <FormGroup>
          <Label>{t("accessRequests.purposeOfVisit")}</Label>
          <Input
            type="textarea"
            rows={5}
            name="purposeOfVisit"
            value={formik.values.purposeOfVisit}
            onChange={formik.handleChange}
          />
        </FormGroup>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <Button color="link" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            className="modal-submit"
            disabled={formik.isSubmitting || !formik.values.residentId}
            loading={formik.isSubmitting}
          >
            {t("accessRequests.submitRequest")} <i className="ri-arrow-right-line ms-2"></i>
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddGuestReqModal;
